import React from 'react';
import { Trash2 } from 'lucide-react';
import type { CartLine } from '../types';
import { formatCents } from '../utils/money';
import { useCart } from '../context/CartContext';

interface Props {
  line: CartLine;
}

const CartLineRow: React.FC<Props> = ({ line }) => {
  const { updateQty, removeLine, lineKey } = useCart();
  const key = lineKey(line);

  return (
    <div className="flex gap-4 py-4 border-b border-background-dark">
      {line.product_image ? (
        <img
          src={line.product_image}
          alt={line.product_name}
          className="w-20 h-20 object-cover rounded bg-background-dark flex-shrink-0"
        />
      ) : (
        <div className="w-20 h-20 rounded bg-background-dark flex-shrink-0" />
      )}

      <div className="flex-1 min-w-0">
        <div className="flex justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{line.product_name}</div>
            <div className="text-xs text-text-light">{line.variant_label}</div>
            {line.personalization && Object.keys(line.personalization).length > 0 && (
              <ul className="text-xs text-text-light mt-1 space-y-0.5">
                {Object.entries(line.personalization).map(([k, v]) => (
                  <li key={k}><span className="capitalize">{k}</span>: {v}</li>
                ))}
              </ul>
            )}
            {/* Shown back, so a customer can check what they asked for
                before ordering rather than trusting it went somewhere. */}
            {line.notes && (
              <p className="text-xs text-text-light mt-1 whitespace-pre-line border-l-2 border-background-dark pl-2">
                {line.notes}
              </p>
            )}
          </div>
          <div className="text-right">
            <div className="text-sm">{formatCents(line.unit_price_cents * line.qty)}</div>
            <div className="text-xs text-text-light">{formatCents(line.unit_price_cents)} ea</div>
          </div>
        </div>

        <div className="flex items-center gap-3 mt-2">
          <input
            type="number"
            min={1}
            value={line.qty}
            onChange={e => updateQty(key, parseInt(e.target.value, 10) || 0)}
            className="w-16 border border-background-dark rounded px-2 py-1 text-sm"
          />
          <button
            type="button"
            onClick={() => removeLine(key)}
            className="text-text-light hover:text-primary"
            aria-label="Remove"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default CartLineRow;
